"use client";

import { globalStyleObj } from "@/app/assets/styles";
import {
  CommonTextAreaField,
  CommonTextInputField,
  FileReuseDialog,
  LabelText,
  LanguageTabSwitcher,
  PageRefresh,
  ServiceCommonFields,
  ServiceFeaturesFieldSection,
  ServiceInfoFieldSection,
  ServiceResourcesFieldSection,
  ServiceTypesFieldSection,
  SubmitButton,
} from "@/components";
import { useErrorToast, useGenerateSlug, useSuccessToast } from "@/lib/hooks";
import { ServiceValidationSchema } from "@/schemas/pagesSchema/serviceValidationSchema";
import { useAppSelector } from "@/store/hooks";
import { getCustomTheme } from "@/utils/colors";
import { usePageRefresh } from "@/utils/refreshPage";
import { getFileSettingsValue } from "@/utils/website-settings-helper";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTheme } from "next-themes";
import { useSearchParams } from "next/navigation";
import React, { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { MdBusinessCenter } from "react-icons/md";

const ServiceForm = ({
  formType = "CREATE_FORM",
  userId,
  languages = [],
  activeLanguageTab = "",
  targetId = "",
  filesList,
  allFiles,
  paginationDetails,
  itemDetails = {},
  createFnc = () => {},
  updateFnc = () => {},
  searchValue,
  selectedFileType,
  adminRole,
  permissionsList,
}) => {
  const searchParams = useSearchParams();
  const activeSection = searchParams.get("tab");

  const { isPending, pageRefresh } = usePageRefresh();
  const { layoutThemePrimaryColorType } = useAppSelector(
    (state) => state.layout
  );
  const { theme } = useTheme();
  const { bgColor, hoverBgColor, textColor, hexCode } = useMemo(
    () => getCustomTheme({ layoutThemePrimaryColorType }),
    [layoutThemePrimaryColorType]
  );
  const isItemDetailsExist = Object.keys(itemDetails).length > 0;
  const isHideInOtherLanguages = useMemo(() => {
    return formType === "CREATE_FORM" ? true : activeLanguageTab === "en";
  }, [activeLanguageTab]);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset,
    setError,
    setValue,
  } = useForm({
    resolver: zodResolver(ServiceValidationSchema),
    defaultValues: itemDetails,
  });
  // Watch Form Data
  const formData = watch();
  const watchedServiceName = watch("service_name") || "";

  // Reset the form state when itemDetails changes
  useEffect(() => {
    if (Object.keys(itemDetails).length > 0 && formType === "UPDATE_FORM") {
      reset(itemDetails);
    }
  }, [itemDetails, reset]);

  // Handle Auto Generated slug value based on the service_name
  useEffect(() => {
    const generateSlugWithTitle = () => {
      if (watchedServiceName && watchedServiceName.length > 0) {
        setError("slug", "");
        const generatedSlug = useGenerateSlug(watchedServiceName);
        setValue("slug", generatedSlug);
      } else {
        setValue("slug", "");
      }
    };

    if (formType === "CREATE_FORM") {
      generateSlugWithTitle();
    }
  }, [watchedServiceName]);

  // Handle Image Change
  const onChangeImageFiled = useCallback(
    (keyname, id) => {
      if (keyname) {
        setValue(keyname, id);
      }
    },
    [setValue]
  );

  // Handle On Change Text Editor Functionality
  const handleOnChangeTextEditor = useCallback(
    (value, keyName) => {
      if (value) {
        setValue(keyName, value);
      } else {
        setValue(keyName, "");
      }
    },
    [setValue]
  );

  // Handle Add Repeatable Field Functionality
  const handleAddRepeatableField = useCallback(
    (keyName, labelNames = []) => {
      const watchedField = watch(keyName) || {}; // Get existing values or an empty object

      // Loop through labelNameList and add values dynamically
      const updatedField = labelNames.reduce((acc, key) => {
        acc[key] = [...(watchedField[key] || []), ""];

        return acc;
      }, {});

      setValue(keyName, {
        ...watchedField,
        ...updatedField,
      });
    },
    [setValue, watch]
  );

  // Handle On Change Repeatable Fields
  const handleOnChangeRepeatableFields = useCallback(
    (keyName, labelName, value, index) => {
      const watchedField = watch(keyName) || {}; // Get existing data

      // Ensure the field exists and is an array
      const updatedField = {
        ...watchedField,
        [labelName]: watchedField[labelName].map((item, i) =>
          i === index ? value : item
        ),
      };

      setValue(keyName, updatedField);
    },
    [setValue, watch]
  );

  // Handle Remove Repeatable Field Functionality
  const handleRemoveRepeatableField = useCallback(
    (keyName, labelNames = [], index) => {
      const watchedField = watch(keyName) || {}; // Get existing values or an empty object

      // Loop through labelNameList and remove the item at the given index
      const updatedField = labelNames.reduce((acc, key) => {
        acc[key] = watchedField[key]?.filter((_, i) => i !== index) || [];

        return acc;
      }, {});

      setValue(keyName, {
        ...watchedField,
        ...updatedField,
      });
    },
    [setValue, watch]
  );

  // Handle Form Validation Errors
  const handleValidationErrors = useCallback(
    (errors) => {
      Object.keys(errors).forEach((field) => {
        setError(field, {
          type: "server",
          message: errors[field].message,
        });
      });
    },
    [setError]
  );

  // Handle Form Submit Functionality
  const onSubmit = async (data) => {
    if (formType === "CREATE_FORM") {
      const response = await createFnc(formData, userId);

      if (response.success) {
        useSuccessToast(response?.message || "Service created successfully.");
        reset();
      } else {
        useErrorToast(response?.message || "Something went wrong");
      }
    }

    if (formType === "UPDATE_FORM") {
      const response = await updateFnc(
        userId,
        targetId,
        formData,
        activeLanguageTab
      );

      if (response.success) {
        useSuccessToast(response?.message || "Service updated successfully.");
        pageRefresh();
      } else {
        useErrorToast(response?.message || "Something went wrong");
      }
    }
  };

  // Handle Refresh Page Loading when update API runs
  if (isPending) {
    return <PageRefresh />;
  }

  // console.log(formData);

  return (
    <div
      className={`${globalStyleObj.backgroundLight900Dark300} w-full rounded-sm shadow-light overflow-hidden`}
    >
      {/* Langugage SearchParams Tab Switcher */}
      {isItemDetailsExist && languages.length > 1 && (
        <LanguageTabSwitcher
          languages={languages}
          colorGrade={{ bgColor, hoverBgColor, textColor, hexCode }}
        />
      )}

      <form onSubmit={handleSubmit(onSubmit)} className={`p-3 sm:p-5`}>
        {/* Common Details (service_name, slug, service_short_description, featured_image, banner_image, service_description, source_link, button_label, order_number) */}
        {activeSection === "section-1" && (
          <ServiceCommonFields
            control={control}
            errors={errors}
            formData={formData}
            filesList={filesList}
            allFiles={allFiles}
            paginationDetails={paginationDetails}
            handleOnChangeTextEditor={handleOnChangeTextEditor}
            handleOnChangeFile={onChangeImageFiled}
            adminRole={adminRole}
            permissionsList={permissionsList}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            isItemDetailsExist={isItemDetailsExist}
            isHideInOtherLanguages={isHideInOtherLanguages}
            activeLanguageTab={activeLanguageTab}
          />
        )}

        {/* Features Section (service_features) */}
        {activeSection === "section-2" && (
          <ServiceFeaturesFieldSection
            control={control}
            errors={errors}
            formData={formData}
            filesList={filesList}
            allFiles={allFiles}
            paginationDetails={paginationDetails}
            handleAddRepeatableField={handleAddRepeatableField}
            handleOnChangeRepeatableFields={handleOnChangeRepeatableFields}
            handleRemoveRepeatableField={handleRemoveRepeatableField}
            adminRole={adminRole}
            permissionsList={permissionsList}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            isItemDetailsExist={isItemDetailsExist}
            isHideInOtherLanguages={isHideInOtherLanguages}
          />
        )}

        {/* Service Types Section (service_types) */}
        {activeSection === "section-3" && (
          <ServiceTypesFieldSection
            control={control}
            errors={errors}
            formData={formData}
            handleAddRepeatableField={handleAddRepeatableField}
            handleOnChangeRepeatableFields={handleOnChangeRepeatableFields}
            handleRemoveRepeatableField={handleRemoveRepeatableField}
            isItemDetailsExist={isItemDetailsExist}
            isHideInOtherLanguages={isHideInOtherLanguages}
          />
        )}

        {/* Service Info Section (service_info_title, service_info_sub_title, service_info_key_points) */}
        {activeSection === "section-4" && (
          <ServiceInfoFieldSection
            control={control}
            errors={errors}
            formData={formData}
            handleAddRepeatableField={handleAddRepeatableField}
            handleOnChangeRepeatableFields={handleOnChangeRepeatableFields}
            handleRemoveRepeatableField={handleRemoveRepeatableField}
            isItemDetailsExist={isItemDetailsExist}
            isHideInOtherLanguages={isHideInOtherLanguages}
          />
        )}

        {/* Service Form Section (service_form_heading, service_form_description, service_form_button_label) */}
        {activeSection === "section-5" && (
          <>
            {/* Service Form Heading */}
            <CommonTextInputField
              fieldName="service_form_heading"
              fieldType="text"
              fieldId={
                isItemDetailsExist
                  ? "service-update-form-heading"
                  : "service-form-heading"
              }
              control={control}
              errors={errors}
              errorsType={errors?.service_form_heading}
              placeholderText="Heading"
              labelName="Form Heading"
              inputBoxMaxWidth="xl:max-w-[500px]"
              translateField={isItemDetailsExist}
            />

            {/* Service Form Description */}
            <CommonTextAreaField
              fieldName="service_form_description"
              fieldId={
                isItemDetailsExist
                  ? "service-update-form-description"
                  : "service-form-description"
              }
              control={control}
              errors={errors}
              errorsType={errors?.service_form_description}
              labelName="Form Description"
              placeholderText="Description"
              extraClass="mt-5"
              inputBoxMaxWidth="xl:max-w-[500px]"
              translateField={isItemDetailsExist}
            />

            {/* Service Form Button Label */}
            <CommonTextInputField
              fieldName="service_form_button_label"
              fieldType="text"
              fieldId={
                isItemDetailsExist
                  ? "service-update-form-button-label"
                  : "service-form-button-label"
              }
              control={control}
              errors={errors}
              errorsType={errors?.service_form_button_label}
              placeholderText="Button Label"
              labelName="Form Button Label"
              extraClass="mt-5"
              inputBoxMaxWidth="xl:max-w-[500px]"
              translateField={isItemDetailsExist}
            />
          </>
        )}

        {/* Service Resources Section (service_registration_resources_heading, service_registration_resources_button_label, service_registration_resources, service_download_resources_heading, service_download_resources_button_label, service_download_resources) */}
        {activeSection === "section-6" && (
          <ServiceResourcesFieldSection
            control={control}
            errors={errors}
            formData={formData}
            filesList={filesList}
            allFiles={allFiles}
            paginationDetails={paginationDetails}
            handleAddRepeatableField={handleAddRepeatableField}
            handleOnChangeRepeatableFields={handleOnChangeRepeatableFields}
            handleRemoveRepeatableField={handleRemoveRepeatableField}
            adminRole={adminRole}
            permissionsList={permissionsList}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            isItemDetailsExist={isItemDetailsExist}
            isHideInOtherLanguages={isHideInOtherLanguages}
          />
        )}

        {/* Service Meta Section (meta_title, meta_image, meta_keywords, meta_description) */}
        {isHideInOtherLanguages && activeSection === "section-7" && (
          <>
            {/* MetaTitle */}
            <CommonTextInputField
              fieldName="meta_title"
              fieldType="text"
              fieldId={
                isItemDetailsExist
                  ? "service-update-meta-title"
                  : "service-meta-title"
              }
              control={control}
              errors={errors}
              errorsType={errors?.meta_title}
              placeholderText="Meta Title"
              labelName="Meta Title"
              inputBoxMaxWidth="xl:max-w-[500px]"
            />

            {/* Meta Image */}
            <div className={`mt-5 ${globalStyleObj.commonInputContainerClass}`}>
              <LabelText
                text="Meta Image"
                htmlForId={
                  isItemDetailsExist
                    ? "service-update-meta-image"
                    : "service-meta-image"
                }
              />

              <div className="flex flex-col gap-2 w-full xl:max-w-[500px]">
                <FileReuseDialog
                  htmlId={
                    isItemDetailsExist
                      ? "service-update-meta-image"
                      : "service-meta-image"
                  }
                  filesList={filesList}
                  allFiles={allFiles}
                  paginationDetails={paginationDetails}
                  searchValue={searchValue}
                  selectedFileType={selectedFileType}
                  onChangeBannerImage={(id) =>
                    handleOnChangeFile("meta_image", id)
                  }
                  selectedBannerFileId={
                    isItemDetailsExist
                      ? (getFileSettingsValue(
                          allFiles,
                          formData?.meta_image || ""
                        )?._id ?? null)
                      : null
                  }
                  selectedBannerFileName={
                    isItemDetailsExist
                      ? (getFileSettingsValue(
                          allFiles,
                          formData?.meta_image || ""
                        )?.fileName ?? null)
                      : null
                  }
                  adminRole={adminRole}
                  permissionsList={permissionsList}
                />
              </div>
            </div>

            {/* Meta Keywords */}
            <CommonTextInputField
              fieldName="meta_keywords"
              fieldType="text"
              fieldId={
                isItemDetailsExist
                  ? "service-update-meta-keywords"
                  : "service-meta-keywords"
              }
              control={control}
              errors={errors}
              errorsType={errors?.meta_keywords}
              placeholderText="Meta Keywords"
              labelName="Meta Keywords"
              extraClass="mt-5"
              extraInformationText="Enter comma separated keywords (e.g. keyword1,keyword2,keyword3)"
              inputBoxMaxWidth="xl:max-w-[500px]"
            />

            {/* Meta Description */}
            <CommonTextAreaField
              fieldName="meta_description"
              fieldId={
                isItemDetailsExist
                  ? "service-update-meta-description"
                  : "service-meta-description"
              }
              control={control}
              errors={errors}
              errorsType={errors?.meta_description}
              labelName="Meta Description"
              placeholderText="Meta Description"
              extraClass="mt-5"
              inputBoxMaxWidth="xl:max-w-[500px]"
            />
          </>
        )}

        <SubmitButton
          isSubmitting={isSubmitting}
          colorGrade={{ bgColor, hoverBgColor, textColor, hexCode }}
          label={formType === "CREATE_FORM" ? "Add Service" : "Update Service"}
          icon={<MdBusinessCenter />}
        />
      </form>
    </div>
  );
};

export default ServiceForm;
